import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ErreurApi, api } from '../lib/apiClient';
import { useCompteGmail } from '../hooks/useCompteGmail';
import { CompteGmail } from './CompteGmail';
import { Champ, Modale } from './Elements';

/**
 * Modification de sa propre fiche, en libre-service.
 *
 * L'identifiant de connexion n'y figure pas : il est la cle
 * d'imputabilite du journal d'audit. Le laisser changer rendrait
 * illisible tout l'historique deja ecrit sous l'ancien.
 */
export function ModificationProfil({
  ouverte,
  onFermer,
}: {
  ouverte: boolean;
  onFermer: () => void;
}) {
  const { session, rafraichir, peut } = useAuth();
  const [valeurs, setValeurs] = useState({
    nom: '',
    prenom: '',
    email: '',
    dashboardDefaut: 'activite',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialise, setInitialise] = useState(false);
  // L'acces Gmail (0.73.0) : charge quand la modale s'ouvre.
  const gmail = useCompteGmail(ouverte);

  // Le choix « Finances » n'est propose que si au moins une habilitation
  // ouvre le dashboard financier — sinon le reglage serait sans effet.
  const financesAccessibles = (session?.entites ?? []).some((e) => e.accesFinances);

  // Recharge les valeurs a chaque ouverture, pour ne pas rouvrir sur une
  // saisie abandonnee.
  if (ouverte && !initialise && session) {
    setInitialise(true);
    setValeurs({
      nom: session.utilisateur.nom,
      prenom: session.utilisateur.prenom,
      email: session.utilisateur.email,
      dashboardDefaut: session.utilisateur.dashboardDefaut,
    });
    setErreur(null);
  }
  if (!ouverte && initialise) setInitialise(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.patch('/api/auth/profil', valeurs);
      await rafraichir();
      onFermer();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Mes informations" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Prenom">
            <input
              className="champ"
              value={valeurs.prenom}
              onChange={(e) => setValeurs({ ...valeurs, prenom: e.target.value })}
              required
              autoFocus
            />
          </Champ>
          <Champ libelle="Nom">
            <input
              className="champ"
              value={valeurs.nom}
              onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
              required
            />
          </Champ>
        </div>

        <Champ libelle="Adresse email">
          <input
            className="champ"
            type="email"
            value={valeurs.email}
            onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
            required
          />
        </Champ>

        {financesAccessibles && (
          <Champ
            libelle="Dashboard à la connexion"
            aide="Celui que le tableau de bord ouvre par défaut. La bascule reste possible à tout moment sur l’écran."
          >
            <select
              className="champ"
              value={valeurs.dashboardDefaut}
              onChange={(e) => setValeurs({ ...valeurs, dashboardDefaut: e.target.value })}
            >
              <option value="activite">Activité</option>
              <option value="finances">Finances</option>
            </select>
          </Champ>
        )}

        <Champ libelle="Identifiant de connexion">
          <input className="champ" value={session?.utilisateur.identifiant ?? ''} disabled />
        </Champ>
        <p className="-mt-2 text-xs text-encre-400">
          L’identifiant n’est pas modifiable : c’est lui qui relie vos actions au journal d’audit.
          Vous pouvez vous connecter indifféremment avec lui ou avec votre adresse email.
        </p>

        <div className="border-t border-encre-100 pt-4">
          <CompteGmail etat={gmail.donnees} recharger={gmail.recharger} permission={peut('message.brouillon')} />
        </div>

        {erreur && (
          <p
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            role="alert"
          >
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
